import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import compOffService from '../services/compOffService';

const CompOffClaims = ({ selectedMonth, filterType = 'monthly', selectedYear, dateRangeStart, dateRangeEnd, onFilteredCountChange, refreshKey, canApplyForEmployee, onApplyForEmployee }) => {
  const [claims, setClaims] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reason, setReason] = useState('');
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [reasonError, setReasonError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchClaims();
  }, [selectedMonth, filterType, selectedYear, dateRangeStart, dateRangeEnd, refreshKey]);

  const fetchClaims = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await compOffService.getMyClaims();
      let fetchedClaims = response.data.claims || [];

      // Set balance from API response
      setBalance(response.data.balance || 0);

      // Apply filter based on filterType
      if (filterType === 'daterange' && dateRangeStart && dateRangeEnd) {
        const start = new Date(dateRangeStart);
        const end = new Date(dateRangeEnd + 'T23:59:59');
        fetchedClaims = fetchedClaims.filter(claim => {
          const claimDate = new Date(claim.workDate);
          return claimDate >= start && claimDate <= end;
        });
      } else if (filterType === 'yearly' && selectedYear) {
        fetchedClaims = fetchedClaims.filter(claim => {
          const claimDate = new Date(claim.workDate);
          return claimDate.getFullYear() === parseInt(selectedYear);
        });
      } else if (selectedMonth) {
        const [year, month] = selectedMonth.split('-');
        fetchedClaims = fetchedClaims.filter(claim => {
          const claimDate = new Date(claim.workDate);
          return claimDate.getFullYear() === parseInt(year) &&
            (claimDate.getMonth() + 1) === parseInt(month);
        });
      }

      setClaims(fetchedClaims);
      if (onFilteredCountChange) {
        onFilteredCountChange(response.data.balance || 0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDate(null);
    setReason('');
    setIsHalfDay(false);
    setReasonError('');
    setError('');
  };

  const handleSubmitClaim = async () => {
    // Validate
    setReasonError('');
    if (!selectedDate) {
      setError('Please select a work date');
      return;
    }
    if (!reason.trim()) {
      setReasonError('Reason is required — please describe the work you did on this day');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      await compOffService.createClaim(formattedDate, reason, isHalfDay);
      setSuccess(`Comp-off ${isHalfDay ? 'half day' : 'full day'} claim submitted successfully!`);
      handleCloseDialog();
      fetchClaims();
    } catch (err) {
      let errorMessage = 'Failed to submit claim';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      console.error('Comp-off claim error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <ApprovedIcon sx={{ color: 'success.main', mr: 1 }} />;
      case 'rejected':
        return <RejectedIcon sx={{ color: 'error.main', mr: 1 }} />;
      default:
        return <PendingIcon sx={{ color: 'warning.main', mr: 1 }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  // Calculate date limits
  const today = new Date();
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

  return (
    <Box>
      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Claims Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            My Comp-Off
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canApplyForEmployee && (
              <Button
                variant="outlined"
                size="small"
                onClick={onApplyForEmployee}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                For Employee
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              size="small"
              sx={{
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Add
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : claims.length === 0 ? (
          <Alert severity="info" icon={<InfoIcon />}>
            No comp-off claims found for the selected period.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="center"><strong>Type</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Reason</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Settlement</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Approved By</strong></TableCell>
                  <TableCell><strong>Expiry</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><strong>Comments</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.map((claim) => (
                  <TableRow key={claim.id} hover>
                    <TableCell>
                      {new Date(claim.workDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={claim.isHalfDay ? 'Half Day' : 'Full Day'}
                        size="small"
                        color={claim.isHalfDay ? 'warning' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Tooltip title={claim.reason}>
                        <span>
                          {claim.reason.length > 40 ? `${claim.reason.substring(0, 40)}...` : claim.reason}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(claim.status)}
                        label={claim.status.toUpperCase()}
                        color={getStatusColor(claim.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {claim.status === 'approved' && (
                        claim.settlementType === 'paid'
                          ? <Chip label="Paid" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }} />
                          : claim.settlementType === 'leave_taken'
                          ? <Chip label="Leave Taken" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                          : <Chip label="Not Settled" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {claim.approver ? claim.approver.fullName : '-'}
                    </TableCell>
                    <TableCell>
                      {claim.expiryDate ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {new Date(claim.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </Typography>
                          {claim.isExpired && (
                            <Chip label="EXPIRED" size="small" color="error" sx={{ mt: 0.5, display: { xs: 'none', sm: 'inline-flex' } }} />
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {claim.managerComments || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Claim Dialog */}
      <Dialog open={openDialog} onClose={() => !loading && handleCloseDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Claim Comp-Off Day
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the date you worked on a holiday or weekend to claim a comp-off day.
            </Typography>

            {/* Work Date */}
            <TextField
              type="date"
              fullWidth
              label="Work Date"
              value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: twoMonthsAgo.toISOString().split('T')[0],
                max: today.toISOString().split('T')[0]
              }}
              sx={{ mb: 2 }}
            />

            {/* Half / Full Day Toggle */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                How long did you work on this day? *
              </Typography>
              <ToggleButtonGroup
                value={isHalfDay ? 'half' : 'full'}
                exclusive
                onChange={(e, val) => { if (val !== null) setIsHalfDay(val === 'half'); }}
                size="small"
                fullWidth
              >
                <ToggleButton value="full" color="primary" sx={{ fontWeight: 600 }}>
                  Full Day
                </ToggleButton>
                <ToggleButton value="half" color="warning" sx={{ fontWeight: 600 }}>
                  Half Day
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Reason - mandatory */}
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label="Reason for working *"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim()) setReasonError('');
              }}
              placeholder="Describe the work you did on this day (e.g., shoot for XYZ project, editing, event coverage, etc.)"
              error={!!reasonError}
              sx={{ mb: 0.5 }}
            />
            {reasonError && (
              <FormHelperText error sx={{ mb: 1, mx: 0 }}>
                {reasonError}
              </FormHelperText>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitClaim}
            variant="contained"
            disabled={loading || !selectedDate}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Submitting...' : 'Submit Claim'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompOffClaims;